import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";

const Bin = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");

  const mobileNo = localStorage.getItem("userMobileNumber") || "";
  const loggedIn = Boolean(mobileNo);

  useEffect(() => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    const fetchBin = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/Product/GetBinProducts?mobileNo=${mobileNo}`
        );
        if (!res.ok) throw new Error("Failed to load recycle bin items");
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Unable to load recycle bin");
      } finally {
        setLoading(false);
      }
    };
    fetchBin();
  }, [loggedIn, mobileNo]);

  const handleRestore = async (id) => {
    try {
      setActionMsg("Restoring product...");
      const res = await fetch(
        `${API_BASE_URL}/Product/RestoreFromBin?id=${id}`,
        { method: "PUT" }
      );
      if (!res.ok) throw new Error("Restore failed");
      setItems((prev) => prev.filter((p) => p.Id !== id));
      setActionMsg("Product restored");
    } catch (err) {
      setActionMsg(err.message || "Restore failed");
    } finally {
      setTimeout(() => setActionMsg(""), 1200);
    }
  };

  const handleDeleteForever = async (id) => {
    if (!window.confirm("Delete this product permanently? This cannot be undone.")) return;
    try {
      setActionMsg("Deleting product...");
      const res = await fetch(
        `${API_BASE_URL}/Product/DeleteProduct?id=${id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Delete failed");
      setItems((prev) => prev.filter((p) => p.Id !== id));
      setActionMsg("Product deleted permanently");
    } catch (err) {
      setActionMsg(err.message || "Delete failed");
    } finally {
      setTimeout(() => setActionMsg(""), 1200);
    }
  };

  if (!loggedIn) {
    return (
      <div style={{ ...styles.center, flexDirection: "column", gap: "14px", minHeight: "70vh" }}>
        <div style={{ fontSize: "40px" }}>🗑️</div>
        <h2 style={{ margin: 0 }}>Please login to view your Recycle Bin</h2>
        <button style={styles.loginBtn} onClick={() => navigate("/login")}>
          Login
        </button>
      </div>
    );
  }

  if (loading) return <div style={styles.center}>Loading recycle bin...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.page}>
      <Seo title="Recycle Bin" description="AIO India recycle bin — deleted products restore karein ya permanently delete karein." path="/bin" />
      <div style={styles.headerRow}>
        <h1 style={{ margin: 0 }}>Recycle Bin</h1>
        {actionMsg && <div style={styles.badge}>{actionMsg}</div>}
      </div>
      {items.length === 0 ? (
        <div style={styles.empty}>No items in your recycle bin.</div>
      ) : (
        <div style={styles.grid}>
          {items.map((p) => (
            <div key={p.Id} style={styles.card}>
              <div style={styles.imageWrap}>
                <img src={p.ProductImage} alt={p.ProductName} style={styles.image} />
                <div style={styles.priceTag}>₹{p.Price}</div>
              </div>
              <div style={styles.info}>
                <div style={styles.name}>{p.ProductName}</div>
                <div style={styles.actions}>
                  <button style={{ ...styles.btn, ...styles.renew }} onClick={() => handleRestore(p.Id)}>
                    Restore
                  </button>
                  <button style={{ ...styles.btn, ...styles.delete }} onClick={() => handleDeleteForever(p.Id)}>
                    Delete Forever
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "20px",
    minHeight: "70vh",
    background: "linear-gradient(135deg, #f6f8ff 0%, #fdf4ff 100%)",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },
  badge: {
    padding: "6px 10px",
    background: "#eef2ff",
    color: "#4338ca",
    borderRadius: "999px",
    fontSize: "12px",
    border: "1px solid #c7d2fe",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    overflow: "hidden",
    border: "1px solid #f1f5f9",
    transition: "transform 180ms ease, box-shadow 180ms ease",
  },
  imageWrap: {
    position: "relative",
    width: "100%",
    height: "100px",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  priceTag: {
    position: "absolute",
    bottom: "8px",
    right: "8px",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    padding: "5px 8px",
    borderRadius: "8px",
    fontWeight: 700,
    fontSize: "13px",
  },
  info: {
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  name: {
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: 1.4,
  },
  actions: {
    display: "flex",
    gap: "8px",
  },
  btn: {
    flex: 1,
    padding: "8px 4px",
    borderRadius: "7px",
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "12px",
    color: "#fff",
  },
  renew: {
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    boxShadow: "0 5px 12px rgba(22,163,74,0.22)",
  },
  delete: {
    background: "linear-gradient(135deg, #ef4444, #dc2626)",
    boxShadow: "0 5px 12px rgba(220,38,38,0.22)",
  },
  empty: {
    padding: "30px",
    textAlign: "center",
    fontWeight: 600,
    color: "#475569",
  },
  center: {
    padding: "40px",
    textAlign: "center",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  error: {
    padding: "40px",
    textAlign: "center",
    fontWeight: 600,
    color: "#ef4444",
  },
  loginBtn: {
    padding: "10px 32px",
    borderRadius: "8px",
    border: "none",
    background: "linear-gradient(135deg, #8a2be2, #ff69b4)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
};

// Hover effects are handled via CSS inline onMouseEnter/onMouseLeave

export default Bin;
